import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { words } from './translations';
import defaultQuestions from './sample.json';

const QuestionInput = ({ saveQuestions, setScreen, language }) => {
  const [questions, setQuestions] = useState(['', '', '']);

  const handleQuestionChange = (index, value) => {
    const newQuestions = [...questions];
    newQuestions[index] = value;
    setQuestions(newQuestions);
  };

  const loadDefaultQuestions = () => {
    // Extract questions from the default questions in sample.json
    const defaultQuestionsText = defaultQuestions.map(q => q.question);
    setQuestions(defaultQuestionsText);
  };

  const handleSubmit = () => {
    // Convert questions to format matching sample.json
    const formattedQuestions = questions.map((question, index) => ({
      question,
      fr: question, // In a real app, you might want to translate these
      es: question, // In a real app, you might want to translate these
      id: index + 1
    }));
    
    saveQuestions(formattedQuestions);
    setScreen();
  };

  const allQuestionsEntered = questions.every(q => q.trim().length > 0);

  return (
    <InputContainer>
      <Title>{language === 'EN' ? 'Custom Survey Questions' : 
              language === 'FR' ? 'Questions d\'enquête personnalisées' :
              'Preguntas de encuesta personalizadas'}</Title>
      
      <Description>
        {language === 'EN' ? 'Please enter 3 questions that will be used in your survey. These questions should reflect what you want to learn about your organization or team.' : 
         language === 'FR' ? 'Veuillez saisir 3 questions qui seront utilisées dans votre enquête. Ces questions doivent refléter ce que vous souhaitez apprendre sur votre organisation ou votre équipe.' :
         'Por favor, ingrese 3 preguntas que se utilizarán en su encuesta. Estas preguntas deben reflejar lo que desea aprender sobre su organización o equipo.'}
      </Description>
      
      <DefaultButtonArea>
        <DefaultButton onClick={loadDefaultQuestions}>
          {language === 'EN' ? 'Use Default Questions' : 
           language === 'FR' ? 'Utiliser les questions par défaut' :
           'Usar preguntas predeterminadas'}
        </DefaultButton>
      </DefaultButtonArea>
      
      <InputsContainer>
        {questions.map((question, index) => (
          <InputGroup key={index}>
            <Label>
              {language === 'EN' ? `Question ${index + 1}` : 
               language === 'FR' ? `Question ${index + 1}` :
               `Pregunta ${index + 1}`}
            </Label>
            <Input 
              type="text" 
              value={question} 
              onChange={(e) => handleQuestionChange(index, e.target.value)} 
              placeholder={
                language === 'EN' ? `Enter your question here...` : 
                language === 'FR' ? `Entrez votre question ici...` :
                `Escriba su pregunta aquí...`
              }
            />
          </InputGroup>
        ))}
      </InputsContainer>

      <ButtonArea>
        <Button onClick={handleSubmit} disabled={!allQuestionsEntered}>
          {language === 'EN' ? 'Continue' : 
           language === 'FR' ? 'Continuer' :
           'Continuar'}
        </Button>
      </ButtonArea>
    </InputContainer>
  );
};

export default QuestionInput;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const InputContainer = styled.div`
  width: 100%;
  max-width: 700px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  opacity: 0;
  animation: ${fadeIn} 1s ease-out forwards;
`;

const Title = styled.div`
  color: #2D70E2;
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 20px;
`;

const Description = styled.p`
  font-size: 16px;
  line-height: 1.5;
  color: #2A3039;
  margin-bottom: 30px;
`;

const DefaultButtonArea = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 20px;
`;

const DefaultButton = styled.button`
  background-color: #f0f7ff;
  color: #2D70E2;
  font-size: 14px;
  font-weight: 500;
  border: 1px solid #2D70E2;
  border-radius: 5px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background-color: #e0f0ff;
  }
`;

const InputsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #2A3039;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #E5E7EB;
  border-radius: 5px;
  font-size: 16px;
  color: #2A3039;
  outline: none;
  transition: border-color 0.3s;
  
  &:focus {
    border-color: #2D70E2;
  }
  
  &::placeholder {
    color: #9CA3AF;
  }
`;

const ButtonArea = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin-top: 30px;
`;

const Button = styled.button`
  background-color: #2D70E2;
  color: white;
  font-size: 16px;
  font-weight: 400;
  border: none;
  border-radius: 5px;
  padding: 10px 35px;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background-color: #1F4E9D;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`; 